'use client'

import { useState } from 'react'
import Link from 'next/link'
import { useTranslation } from 'react-i18next'
import { Button } from '@planner/ui'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { Menu, X } from 'lucide-react'

export function LandingHeader() {
  const { t } = useTranslation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
      setMobileMenuOpen(false)
    }
  }

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border/50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <div className="container flex h-16 items-center justify-between px-4">
        <div className="flex items-center gap-8">
          <Link href="/" className="flex items-center space-x-2">
            <img 
              src="/logo%20banquieto.png" 
              alt="Banquieto" 
              className="h-10 w-auto"
              width={120}
              height={40}
              loading="eager"
              fetchPriority="high"
            />
          </Link>
        
          <nav className="hidden md:flex items-center gap-6">
            <button
              onClick={() => scrollToSection('features')}
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {t('landing.header.nav.features')}
            </button>
            <button
              onClick={() => scrollToSection('partners')}
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {t('landing.header.nav.partners')}
            </button>
            <button
              onClick={() => scrollToSection('pricing')}
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {t('landing.header.nav.pricing')}
            </button>
            <button
              onClick={() => scrollToSection('testimonials')}
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {t('landing.header.nav.testimonials')}
            </button>
            <button
              onClick={() => scrollToSection('faq')}
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {t('landing.header.nav.faq')}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {t('landing.header.nav.contact')}
            </button>
          </nav>
        </div>

        <div className="flex items-center gap-4">
          <div className="hidden md:block">
            <LanguageSwitcher />
          </div>
          <Link href="/login">
            <Button variant="ghost" className="hidden md:inline-flex">
              {t('landing.header.login')}
            </Button>
          </Link>
          <Link href="/register">
            <Button className="hidden md:inline-flex bg-green-600 hover:bg-green-700 text-white">
              {t('landing.header.getStarted')}
            </Button>
          </Link>
          
          <button
            className="md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <Menu className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden border-t bg-background">
          <nav className="container flex flex-col gap-4 px-4 py-4">
            <button
              onClick={() => scrollToSection('features')}
              className="text-left text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {t('landing.header.nav.features')}
            </button>
            <button
              onClick={() => scrollToSection('partners')}
              className="text-left text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {t('landing.header.nav.partners')}
            </button>
            <button
              onClick={() => scrollToSection('pricing')}
              className="text-left text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {t('landing.header.nav.pricing')}
            </button>
            <button
              onClick={() => scrollToSection('testimonials')}
              className="text-left text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {t('landing.header.nav.testimonials')}
            </button>
            <button
              onClick={() => scrollToSection('faq')}
              className="text-left text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {t('landing.header.nav.faq')}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-left text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {t('landing.header.nav.contact')}
            </button>
            <div className="flex items-center gap-4 pt-4 border-t">
              <LanguageSwitcher />
              <Link href="/login" className="flex-1">
                <Button variant="ghost" className="w-full">
                  {t('landing.header.login')}
                </Button>
              </Link>
              <Link href="/register" className="flex-1">
                <Button className="w-full">
                  {t('landing.header.getStarted')}
                </Button>
              </Link>
            </div>
          </nav>
        </div>
      )}
    </header>
  )
}

